
import { JUVAnalyticsEvent, PerformanceMetrics } from '@/types/analytics';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export const GA_TRACKING_ID = import.meta.env.VITE_GA_TRACKING_ID || 'G-XXXXXXXXXX';

// Initialize Google Analytics
export const initializeAnalytics = () => {
  if (typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: any[]) {
    window.dataLayer.push(args);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', GA_TRACKING_ID, {
    page_title: document.title,
    page_location: window.location.href,
    custom_map: {
      custom_dimension_1: 'user_type',
      custom_dimension_2: 'location',
      custom_dimension_3: 'membership_tier'
    }
  });
};

// Track custom JUV events
export const trackEvent = (event: JUVAnalyticsEvent) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', event.action, {
    event_category: event.category,
    event_label: event.label,
    value: event.value,
    custom_dimension_1: event.user_type,
    custom_dimension_2: event.location,
    custom_dimension_3: event.membership_tier,
    treatment_type: event.treatment_type,
    credit_amount: event.credit_amount
  });
};

// Track page views
export const trackPageView = (path: string, title?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('config', GA_TRACKING_ID, {
    page_path: path,
    page_title: title || document.title
  });
};

// Track conversions
export const trackConversion = (conversionAction: string, value?: number) => {
  trackEvent({
    action: 'conversion',
    category: 'ecommerce',
    label: conversionAction,
    value
  });
};

// Track JUV-specific events
export const trackQuizCompletion = (results: string, userType?: string) => {
  trackEvent({
    action: 'quiz_complete',
    category: 'engagement',
    label: results,
    user_type: userType as any
  });
};

export const trackTreatmentView = (treatmentName: string, location?: string) => {
  trackEvent({
    action: 'view_treatment',
    category: 'content',
    treatment_type: treatmentName,
    location
  });
};

export const trackMembershipSelection = (tier: string, value?: number) => {
  trackEvent({
    action: 'select_membership',
    category: 'membership',
    membership_tier: tier,
    value
  });
};

export const trackAppointmentBooking = (treatmentType: string, location: string) => {
  trackEvent({
    action: 'book_appointment',
    category: 'conversion',
    treatment_type: treatmentType,
    location
  });
};

export const trackVideoEngagement = (videoId: string, duration: number) => {
  trackEvent({
    action: 'video_engagement',
    category: 'content',
    label: videoId,
    value: duration
  });
};

export const trackCreditUsage = (amount: number, treatmentType?: string) => {
  trackEvent({
    action: 'use_credits',
    category: 'ecommerce',
    credit_amount: amount,
    treatment_type: treatmentType
  });
};

// Track performance metrics
export const trackPerformance = (metrics: PerformanceMetrics) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  Object.entries(metrics).forEach(([metric, value]) => {
    if (value !== undefined) {
      window.gtag('event', 'timing_complete', {
        name: metric.toUpperCase(),
        value: Math.round(value)
      });
    }
  });
};
